<template>
  <div v-if="!pageLoading" class="flsa-survey-search">
    <template v-if="!textFilter">
      <v-text-field
        :model-value="localInputValue"
        class="flsa-survey-search-field"
        density="compact"
        flat
        hide-details
        placeholder="Search job profiles..."
        variant="outlined"
        @update:model-value="localInputValue = $event"
        @keydown.enter.prevent="submitSearch"
      >
        <template #append-inner>
          <DpplNewButton
            color="primary"
            size="small"
            class="apply-search-button"
            @click="submitSearch"
          >
            Apply
          </DpplNewButton>
        </template>
      </v-text-field>
    </template>
    <div
      v-else
      class="search-filter-chip"
    >
      <v-icon
        icon="$magnify"
        class="decor pl-1"
        :size="20"
      />
      <p class="button-content">
        "{{ textFilter }}"
      </p>
      <button
        type="button"
        class="remove-search-button pr-1"
        @click.stop="clearSearch"
      >
        <v-icon
          icon="$close"
          :size="14"
        />
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useFlsaSurveysStore } from '@/stores/jobProfiles/flsaSurveys';
import { useAppLoading } from '@/stores/app/loading';
import { useFilters } from '@/stores/filters/filters';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';

const router = useRouter();
const { sendFilterRequest } = useFilters();
const { setTextFilter } = useFlsaSurveysStore();
const { textFilter } = storeToRefs(useFlsaSurveysStore());
const { pageLoading } = storeToRefs(useAppLoading());

const localInputValue = ref('');

const submitSearch = () => {
  const value = localInputValue.value.trim();

  setTextFilter(value);
  sendFilterRequest({ router });
};

const clearSearch = () => {
  setTextFilter('');
  localInputValue.value = '';
  sendFilterRequest({ router });
};

watch(() => textFilter.value, (newVal, oldVal) => {
  const newTextFilter = newVal || '';
  const oldTextFilter = oldVal || '';

  if (newTextFilter !== oldTextFilter) {
    localInputValue.value = newTextFilter;
  }
});
</script>

<style lang="less" scoped>
.flsa-survey-search {
  display: inline-flex;
}

.flsa-survey-search-field {
  min-width: 260px;

  :deep(.v-field) {
    background-color: @bg-default;
    border: 1px solid @stroke-default;
    border-radius: 4px;
    height: 36px;
    min-height: 36px;
    padding: 0 6px 0 12px;
  }

  :deep(.v-field__field) {
    align-items: center;
  }

  :deep(.v-field__input) {
    padding: 0;
    font-size: 14px;
    min-height: 0;
    color: @text-darkest;

    &::placeholder {
      color: @text-light;
    }
  }

  :deep(.v-field__outline) {
    display: none;
  }

  :deep(.v-field__append-inner) {
    padding-left: 12px;
    display: flex;
    align-items: center;
    align-self: stretch;
  }

  .apply-search-button {
    height: 28px;
  }
}

.search-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  border: 1px solid @stroke-default;
  background-color: @bg-brand-light;
  border-radius: 32px;
  padding: 0 12px;

  p {
    color: @text-brand;
  }

  :deep(.decor) path {
    fill: @icon-brand;
  }
}

.button-content {
  font-size: 13px;
  font-weight: 500;
}

.remove-search-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background-color: transparent;
  margin-left: 4px;
  border-radius: 50%;

  :deep(.v-icon) path {
    fill: @icon-default;
  }

  &:hover {
    cursor: pointer;
  }
}
</style>
